import React from 'react';
import FormControl from '../components/common/FormControl';
import { FieldValidationApi } from './useFieldValidation';
import { maxLenForCode } from './engine';

interface BaseProps {
  vh: FieldValidationApi;
  name: string;
  code: number;
  required?: boolean;
  additional?: Record<string, any> | null;
}

/** Text/email/number input with on-blur engine validation + inline error. */
export const ValidatedInput: React.FC<BaseProps & {
  label?: string; value: any; type?: string; placeholder?: string;
  disabled?: boolean; 'data-testid'?: string; leadingIcon?: React.ReactNode;
  className?: string; maxLength?: number; style?: React.CSSProperties;
  helper?: React.ReactNode; noMargin?: boolean;
  min?: string | number; max?: string | number;
  step?: string | number; size?: string;
  /** Optional callback invoked after engine blur validation — use for domain checks (e.g. uniqueness). */
  onAfterBlur?: (value: string) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ vh, name, code, required, additional, value, onChange, onAfterBlur, ...rest }) => {
  const { maxLength, ...other } = rest as any;
  return (
    <FormControl.Input
      {...other}
      name={name}
      required={required}
      maxLength={maxLength ?? maxLenForCode(code)}
      value={value ?? ''}
      error={vh.errors[name]}
      onChange={(e) => { onChange(e); vh.clearError(name); }}
      onBlur={(e) => { vh.validateField(name, code, e.target.value, { additional, required }); onAfterBlur?.(e.target.value); }}
    />
  );
};

/** Multiline text with on-blur validation. */
export const ValidatedTextarea: React.FC<BaseProps & {
  label?: string; value: any; rows?: number; placeholder?: string;
  disabled?: boolean; 'data-testid'?: string; className?: string; maxLength?: number;
  style?: React.CSSProperties; noResize?: boolean;
  /** Optional callback invoked after engine blur validation — use for domain checks (e.g. uniqueness). */
  onAfterBlur?: (value: string) => void;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}> = ({ vh, name, code, required, additional, value, onChange, onAfterBlur, ...rest }) => {
  const { maxLength, ...other } = rest as any;
  return (
    <FormControl.Textarea
      {...other}
      name={name}
      required={required}
      maxLength={maxLength ?? maxLenForCode(code)}
      value={value ?? ''}
      error={vh.errors[name]}
      onChange={(e) => { onChange(e); vh.clearError(name); }}
      onBlur={(e) => { vh.validateField(name, code, e.target.value, { additional, required }); onAfterBlur?.(e.target.value); }}
    />
  );
};

/** Date field with on-blur validation. `value` is an ISO "YYYY-MM-DD" string. */
export const ValidatedDatePicker: React.FC<BaseProps & {
  label?: string; value?: string; placeholder?: string;
  disabled?: boolean; 'data-testid'?: string; className?: string;
  id?: string; minDate?: Date; maxDate?: Date;
  /** Optional callback invoked after engine blur validation — use for cross-field checks. */
  onAfterBlur?: (value: string) => void;
  onChange: (e: { target: { value: string } }) => void;
}> = ({ vh, name, code, required, additional, value, onChange, onAfterBlur, ...rest }) => (
  <FormControl.DatePicker
    {...rest}
    required={required}
    value={value}
    error={vh.errors[name]}
    onChange={(e) => { onChange(e); vh.clearError(name); }}
    onBlur={() => { vh.validateField(name, code, value, { additional, required }); onAfterBlur?.(value ?? ''); }}
  />
);
